/**
 * TabPicker API Custom Fetch Mutator
 * 
 * Provides a custom fetch implementation for generated API hooks with:
 * - Automatic authentication header management
 * - Consistent error handling
 * - Request/response interceptors
 * - Environment-aware base URL handling
 * 
 * @module TabPicker.APIMutator
 */

// Import types will be added after hooks generation
// import type { ErrorType, BodyType } from './hooks';

/**
 * Custom error class for API errors with structured error information
 */
export class ApiError extends Error {
  public status: number;
  public statusText: string;
  public data?: unknown;

  constructor(status: number, statusText: string, data?: unknown) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

/**
 * Get the base URL for API requests based on environment
 */
function getBaseUrl(): string {
  // In development, use the full-stack dev server
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8788';
  }
  
  // In production, use relative URLs (same origin)
  return '';
}

/**
 * Custom fetch implementation with authentication and error handling
 * 
 * Features:
 * - Automatic authentication via cookies (handled by browser)
 * - Structured error handling with typed responses
 * - Request/response logging in development
 * - AbortController support for request cancellation
 * 
 * @param url - API endpoint URL
 * @param options - Fetch options with custom properties
 * @returns Promise resolving to response data
 */
interface OrvalRequestConfig {
  url: string;
  method?: string;
  headers?: Record<string, string>;
  data?: unknown;
  params?: Record<string, unknown>;
  signal?: AbortSignal;
}

export const customFetch = async <T = unknown>(
  config: OrvalRequestConfig,
  options?: RequestInit & {
    signal?: AbortSignal;
  }
): Promise<T> => {
  const baseUrl = getBaseUrl();
  const fullUrl = config.url.startsWith('http') ? config.url : `${baseUrl}${config.url}`;

  // Build query string from params
  let url = fullUrl;
  if (config.params) {
    const searchParams = new URLSearchParams();
    Object.entries(config.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
  }

  // Default headers with JSON content type
  const headers = new Headers(options?.headers || {});
  if (config.headers) {
    Object.entries(config.headers).forEach(([key, value]) => {
      headers.set(key, value);
    });
  }
  if (!headers.has('Content-Type') && options?.body) {
    headers.set('Content-Type', 'application/json');
  }

  // Include credentials for cookie-based authentication
  const fetchOptions: RequestInit = {
    ...options,
    method: config.method || 'GET',
    headers,
    credentials: 'include', // Send cookies with requests
    signal: config.signal || options?.signal,
  };

  // Add request body if provided
  if (config.data) {
    fetchOptions.body = JSON.stringify(config.data);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  // Development logging
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔄 API Request: ${fetchOptions.method} ${url}`);
    if (fetchOptions.body) {
      console.log('📤 Request Body:', fetchOptions.body);
    }
  }

  try {
    const response = await fetch(url, fetchOptions);
    
    // Development response logging
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.status} ${response.statusText}`);
    }

    // Handle non-JSON responses (like redirects)
    const contentType = response.headers.get('content-type');
    
    if (!response.ok) {
      let errorData;
      
      // Try to parse error response as JSON
      if (contentType?.includes('application/json')) {
        try {
          errorData = await response.json();
        } catch {
          // Ignore JSON parsing errors for error responses
        }
      }
      
      throw new ApiError(response.status, response.statusText, errorData);
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204 || !contentType?.includes('application/json')) {
      return {} as T;
    }

    // Parse and return JSON response
    const data = await response.json();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('📥 Response Data:', data);
    }
    
    return data as T;
    
  } catch (error) {
    // Re-throw ApiError instances without modification
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Handle network errors, timeouts, etc.
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API Request Failed:', error);
    }
    
    // Convert generic errors to ApiError for consistent handling
    throw new ApiError(
      0,
      error instanceof Error ? error.message : 'Network Error',
      error
    );
  }
};

export default customFetch;